import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Tabs } from '@dreamtree-org/twreact-ui';
import { Plus } from 'lucide-react';
import { PageHeader } from '@/components';
import Can from '@rbac/Can';
import { useCan } from '@rbac/useCan';
import Listings from './Listings';
import Inventory from './Inventory';
import ProductModels from './ProductModels';
import Brands from './Brands';
import SizeCharts from './SizeCharts';
import { useQuery } from '@/lib/useQuery';
import { useProductTypes } from '@/lib/productTypes';
import { PRODUCT_TABS, visibleTabs } from '../lib/productTabs';
import { sizedStoreTypes } from '../lib/sizeCharts';

const BODIES = { listed: Listings, stock: Inventory, models: ProductModels, brands: Brands, size_charts: SizeCharts };

/**
 * One catalogue screen (D37): Listed / In stock / Models as tabs, each shown only with
 * its permission. The tab lives in ?tab= so old /inventory, /listings and /models links
 * land on the right one. Adding anything starts from "Add listing" (/add).
 */
export default function Products() {
  const nav = useNavigate();
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const registry = useProductTypes();
  const storeTypes = useQuery('storeProductTypes', {}, { select: (r) => (r && r.data) || [] });
  // Size charts only where the store sells a type by size (MERCH-88).
  const tabs = visibleTabs(PRODUCT_TABS, can, { sized: sizedStoreTypes(registry.byKey, storeTypes.data).length > 0 });
  const index = Math.max(0, tabs.findIndex((t) => t.key === params.get('tab')));
  const Body = tabs.length ? BODIES[tabs[index].key] : null;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Products"
        subtitle="Everything you sell: live listings, devices in stock, your model templates and brands."
        actions={<Can module="inventory" action="create"><Button leftIcon={<Plus size={16} />} onClick={() => nav('/add')}>Add listing</Button></Can>}
      />
      {/* Phones: the tab strip scrolls inside itself, never the page (MERCH-58). */}
      <div className="overflow-x-auto -mx-1 px-1">
        <Tabs index={index} onChange={(i) => setParams({ tab: tabs[i].key }, { replace: true })} variant="line" orientation="horizontal" animated={false}>
          <Tabs.List className="min-w-max border-b border-[var(--app-border)]">
            {tabs.map((t) => <Tabs.Tab key={t.key} tabClassName="min-h-[44px] whitespace-nowrap">{t.label}</Tabs.Tab>)}
          </Tabs.List>
        </Tabs>
      </div>
      {Body ? <Body embedded key={tabs[index].key} /> : null}
    </div>
  );
}
